import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  Box,
  Button,
  Typography,
  Stack,
  styled,
  CircularProgress,
  Paper,
  Avatar,
  alpha,
  useTheme,
  Tooltip,
  Dialog,
  DialogContent,
  IconButton,
} from "@mui/material";
import {
  CloudUpload as UploadIcon,
  PictureAsPdf as PdfIcon,
  Description as DocIcon,
  Delete as DeleteIcon,
  Description as DescriptionIcon,
  Close as CloseIcon,
  Visibility as ViewIcon,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";

import { QK } from "../../../base/qk";
import { getCandidatesbyId, updateCandidate } from "../../../api/services/candidate";
import ConfirmModal from "../../../components/ConfirmModal/ConfirmModal";
import { minioService } from "../../../api/services/minioService";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const CandidatesCv = ({ clickedId }) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [confirm, setConfirm] = useState({ show: false, file: null, action: null });
  const [uploading, setUploading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");

  const { data: candidateData, isLoading: isCandidateLoading } = useQuery(
    [QK.candidate, clickedId],
    () => getCandidatesbyId(clickedId),
    { enabled: !!clickedId }
  );

  const cvMutation = useMutation(
    (data) => updateCandidate(data, clickedId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QK.candidate, clickedId]);
        enqueueSnackbar("CV updated successfully", { variant: "success" });
        setUploading(false);
      },
      onError: (error) => {
        const errorMessage = error.response?.data?.message || "Failed to update CV";
        enqueueSnackbar(errorMessage, { variant: "error" });
        setUploading(false);
      },
    }
  );

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = [".pdf", ".doc", ".docx"];
    const fileExt = "." + file.name.split(".").pop().toLowerCase();
    if (!validTypes.includes(fileExt)) {
      enqueueSnackbar("Please upload PDF, DOC, or DOCX files only", { variant: "error" });
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      enqueueSnackbar("File size should be less than 10MB", { variant: "error" });
      return;
    }

    setConfirm({ show: true, file, action: "upload" });
  };

  const handleRemoveClick = () => {
    setConfirm({ show: true, file: null, action: "remove" });
  };

  const handleConfirmAction = async () => {
    if (confirm.action === "remove") {
      await handleRemoveCV();
      return;
    }

    if (!confirm.file) return;

    setUploading(true);

    try {
      const response = await minioService.uploadFile(confirm.file, "candidate_cv");

      if (response.success) {
        const cvUrl = response.data.url;

        const formData = new FormData();
        formData.append("cv", cvUrl);

        await cvMutation.mutateAsync(formData);
      } else {
        throw new Error(response.message || "Upload failed");
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || "Failed to upload CV";
      enqueueSnackbar(errorMessage, { variant: "error" });
      setUploading(false);
    } finally {
      setConfirm({ show: false, file: null, action: null });
    }
  };

  const handleRemoveCV = async () => {
    try {
      const formData = new FormData();
      formData.append("cv", "");

      await cvMutation.mutateAsync(formData);
      enqueueSnackbar("CV removed successfully", { variant: "success" });
    } catch (error) {
      enqueueSnackbar("Failed to remove CV", { variant: "error" });
    } finally {
      setConfirm({ show: false, file: null, action: null });
    }
  };

  const handlePreview = () => {
    if (cvUrl) {
      setPreviewUrl(cvUrl);
      setPreviewOpen(true);
    }
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewUrl("");
  };

  const cvUrl = candidateData?.data?.cv;
  const fileName = cvUrl ? cvUrl.split("/").pop() : "No document attached";

  if (isCandidateLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2.5 }}>
      <ConfirmModal
        show={confirm.show}
        handleClose={() => setConfirm({ show: false, file: null, action: null })}
        handleConfirm={handleConfirmAction}
        isLoading={uploading || cvMutation.isLoading}
        title={confirm.action === "remove" ? "Remove CV" : "Upload Document"}
        body={
          confirm.action === "remove"
            ? "Are you sure you want to remove the CV? This action cannot be undone."
            : `Are you sure you want to upload "${confirm.file?.name}" as CV?`
        }
        confirmText={confirm.action === "remove" ? "Remove" : "Upload"}
        isDelete={confirm.action === "remove"}
      />

      {/* Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={handleClosePreview}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            maxHeight: "90vh",
            bgcolor: "#1A1A2E",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
            borderBottom: "1px solid rgba(255,255,255,0.1)",
          }}
        >
          <Typography variant="subtitle1" fontWeight={600} color="white">
            CV Preview
          </Typography>
          <IconButton onClick={handleClosePreview} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ p: 2, display: "flex", justifyContent: "center", alignItems: "center" }}>
          {previewUrl?.toLowerCase().endsWith(".pdf") ? (
            <Box sx={{ width: "100%", height: "70vh" }}>
              <iframe
                src={previewUrl}
                width="100%"
                height="100%"
                style={{ border: "none", borderRadius: "8px" }}
                title="CV Preview"
              />
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                p: 4,
                gap: 2,
              }}
            >
              <DocIcon sx={{ fontSize: 80, color: "rgba(255,255,255,0.3)" }} />
              <Typography variant="body1" color="rgba(255,255,255,0.7)">
                Preview not available for this file type
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Paper
        elevation={0}
        sx={{
          p: 4,
          borderRadius: 2,
          textAlign: "center",
          border: "2px dashed",
          borderColor: cvUrl ? theme.palette.primary.main : "#E2E8F0",
          bgcolor: cvUrl ? alpha(theme.palette.primary.main, 0.02) : "#F8FAFC",
          transition: "all 0.3s ease",
          "&:hover": {
            borderColor: theme.palette.primary.main,
          },
        }}
      >
        <Stack spacing={3} alignItems="center">
          {/* Icon */}
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              bgcolor: cvUrl
                ? alpha(theme.palette.primary.main, 0.12)
                : "#F1F5F9",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: cvUrl ? theme.palette.primary.main : "#94A3B8",
            }}
          >
            {cvUrl?.toLowerCase().endsWith(".pdf") ? (
              <PdfIcon sx={{ fontSize: 40 }} />
            ) : cvUrl ? (
              <DocIcon sx={{ fontSize: 40 }} />
            ) : (
              <DescriptionIcon sx={{ fontSize: 40 }} />
            )}
          </Box>

          {/* Title */}
          <Box>
            <Typography variant="h6" fontWeight={700} color="#1A1A2E">
              {cvUrl ? "Curriculum Vitae" : "No CV Uploaded"}
            </Typography>
            <Typography
              variant="body2"
              color="#6B7280"
              sx={{ mt: 0.5, fontFamily: "monospace", fontSize: "0.8rem" }}
            >
              {fileName}
            </Typography>
            {cvUrl && (
              <Typography
                variant="caption"
                color="#6B7280"
                sx={{ mt: 0.5, display: "block" }}
              >
                Stored in secure cloud storage
              </Typography>
            )}
          </Box>

          {/* Actions */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            sx={{ pt: 1, width: "100%", maxWidth: 450 }}
          >
            {cvUrl && (
              <>
                <Tooltip title="Preview CV" arrow>
                  <Button
                    variant="outlined"
                    size="medium"
                    startIcon={<ViewIcon />}
                    onClick={handlePreview}
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      borderRadius: "8px",
                      borderColor: "#D0D5DD",
                      color: "#344054",
                      flex: 1,
                      "&:hover": {
                        borderColor: "#2469F0",
                        color: "#2469F0",
                        bgcolor: alpha("#2469F0", 0.04),
                      },
                    }}
                  >
                    Preview
                  </Button>
                </Tooltip>
                <Tooltip title="Remove CV" arrow>
                  <Button
                    variant="outlined"
                    color="error"
                    size="medium"
                    startIcon={<DeleteIcon />}
                    onClick={handleRemoveClick}
                    disabled={uploading || cvMutation.isLoading}
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      borderRadius: "8px",
                      borderColor: "#FEE2E2",
                      color: "#EF4444",
                      flex: 0.6,
                      "&:hover": {
                        borderColor: "#EF4444",
                        bgcolor: alpha("#EF4444", 0.04),
                      },
                    }}
                  >
                    Remove
                  </Button>
                </Tooltip>
              </>
            )}

            <Button
              component="label"
              variant={cvUrl ? "outlined" : "contained"}
              size="medium"
              startIcon={uploading ? <CircularProgress size={18} /> : <UploadIcon />}
              disabled={uploading || cvMutation.isLoading}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "8px",
                flex: cvUrl ? 0.8 : 1,
                bgcolor: cvUrl ? "transparent" : "#2469F0",
                borderColor: cvUrl ? "#D0D5DD" : "transparent",
                color: cvUrl ? "#344054" : "white",
                px: cvUrl ? 0 : 3,
                minWidth: cvUrl ? "auto" : "100%",
                "&:hover": {
                  bgcolor: cvUrl ? "#F9FAFB" : "#1A5BD4",
                  borderColor: cvUrl ? "#98A2B3" : "transparent",
                },
              }}
            >
              {uploading ? "Uploading..." : cvUrl ? "Upload New" : "Upload CV"}
              <VisuallyHiddenInput
                type="file"
                onChange={handleFileSelect}
                accept=".pdf,.doc,.docx"
              />
            </Button>
          </Stack>

          <Typography
            variant="caption"
            color="#94A3B8"
            sx={{ mt: 1, fontSize: "0.65rem" }}
          >
            Supported formats: PDF, DOC, DOCX (Max size: 10MB)
          </Typography>
        </Stack>
      </Paper>
    </Box>
  );
};

export default CandidatesCv;